import {
  ColumnWidthOutlined,
  InfoCircleOutlined,
  LineChartOutlined,
  ReloadOutlined,
  SplitCellsOutlined,
} from '@ant-design/icons';
import { Button, Popover, Tooltip } from 'antd';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { CHART_COLORS } from '../../../constants/styles';
import { DashboardDisplayMode } from '../../../types/phlebotomist-dashboard';

/** Mobile breakpoint — at or below this we show tap-to-reveal info icons */
const MOBILE_BREAKPOINT = 770;

interface ICompareModeTabsProps {
  displayMode: DashboardDisplayMode;
  onModeChange: (mode: DashboardDisplayMode) => void;
  onReset: () => void;
  /** Disable split screen button (e.g., when screen is too narrow) */
  splitDisabled?: boolean;
  /** Tooltip to show when split is disabled */
  splitDisabledTooltip?: string;
  /** Hide inline compare button (e.g., for Collection Team users) */
  hideInlineCompare?: boolean;
  /** Current window width for mobile info icon behavior */
  windowWidth?: number;
}

/** Mobile info icon — appears as an extension of the button with a vertical divider */
const MobileInfoIcon: FC<{ text: string; isActive?: boolean }> = ({
  text,
  isActive = false,
}) => (
  <Popover
    content={
      <span style={{ maxWidth: 220, display: 'block', color: '#e8ecef' }}>
        {text}
      </span>
    }
    trigger="click"
    placement="bottom"
    overlayStyle={{ maxWidth: 250 }}
    overlayInnerStyle={{
      backgroundColor: '#0e4d8f',
      color: '#e8ecef',
    }}
  >
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 32, // Match antd Button default height
        width: 28,
        marginLeft: -1,
        borderTop: '1px solid #0e4d8f',
        borderRight: '1px solid #0e4d8f',
        borderBottom: '1px solid #0e4d8f',
        borderLeft: '1px solid rgba(255,255,255,0.6)',
        borderTopRightRadius: 4,
        borderBottomRightRadius: 4,
        backgroundColor: '#0e4d8f',
        cursor: 'pointer',
      }}
    >
      <InfoCircleOutlined
        style={{
          color: '#e8ecef',
          fontSize: 13,
        }}
      />
    </span>
  </Popover>
);

export const CompareModeTabs: FC<ICompareModeTabsProps> = ({
  displayMode,
  onModeChange,
  onReset,
  splitDisabled = false,
  splitDisabledTooltip,
  hideInlineCompare = false,
  windowWidth,
}) => {
  const { t } = useTranslation('dashboard.PhlebotomistDashboard');

  const isCompareActive = displayMode !== 'normal';
  const isTrendsMode = displayMode === 'trends';
  const isMobile = (windowWidth ?? window.innerWidth) < MOBILE_BREAKPOINT;

  // Richer tooltip descriptions
  const tooltips = {
    reset: t(
      'compareModes.resetTooltipRich',
      'Return to the default single-view layout and clear any comparison selections'
    ),
    split: splitDisabled
      ? splitDisabledTooltip ||
        t(
          'compareModes.splitDisabledTooltip',
          'Split screen is not available at this screen size'
        )
      : t(
          'compareModes.splitTooltipRich',
          'Open two side-by-side panes with independent filters so you can compare different date ranges, regions, or phlebotomists'
        ),
    inline: t(
      'compareModes.inlineTooltipRich',
      'Add comparison columns directly within each card to see current vs. comparison values and their change side-by-side'
    ),
    trends: t(
      'compareModes.trendsTooltipRich',
      'Switch to historical trend charts showing draw success, unit success, and product yield over configurable time intervals'
    ),
  };

  // URL sync is handled by the context provider's useEffect
  const handleModeChange = useCallback(
    (mode: DashboardDisplayMode) => {
      onModeChange(mode);
    },
    [onModeChange]
  );

  const handleReset = useCallback(() => {
    onReset();
  }, [onReset]);

  /** Wrap a button with either a hover Tooltip (desktop) or append a tap info icon (mobile) */
  const withTooltip = (
    button: React.ReactElement,
    text: string,
    key?: string,
    isActive?: boolean
  ) => {
    if (isMobile) {
      return (
        <span
          key={key}
          style={{ display: 'inline-flex', alignItems: 'center' }}
        >
          {button}
          <MobileInfoIcon text={text} isActive={isActive} />
        </span>
      );
    }
    return (
      <Tooltip key={key} title={text} placement="top">
        {button}
      </Tooltip>
    );
  };

  // On mobile, button right side merges with info icon
  const mobileRightFlat = isMobile
    ? { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
    : {};

  // Active button styling helper
  const activeStyle = {
    backgroundColor: CHART_COLORS.displayModeButton.active,
    borderColor: CHART_COLORS.displayModeButton.active,
    color: CHART_COLORS.displayModeButton.activeText,
    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)',
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
      {withTooltip(
        <Button
          icon={<ReloadOutlined />}
          onClick={handleReset}
          disabled={!isCompareActive}
          style={mobileRightFlat}
        >
          {t('compareModes.reset', 'Reset')}
        </Button>,
        tooltips.reset,
        'reset',
        false
      )}
      {withTooltip(
        <Button
          icon={<SplitCellsOutlined />}
          disabled={splitDisabled}
          onClick={() =>
            handleModeChange(displayMode === 'split' ? 'normal' : 'split')
          }
          style={{
            ...(displayMode === 'split' ? activeStyle : {}),
            ...mobileRightFlat,
          }}
        >
          {t('compareModes.split', 'Split Screen')}
        </Button>,
        tooltips.split,
        'split',
        displayMode === 'split'
      )}
      {!hideInlineCompare &&
        withTooltip(
          <Button
            icon={<ColumnWidthOutlined />}
            onClick={() =>
              handleModeChange(displayMode === 'inline' ? 'normal' : 'inline')
            }
            style={{
              ...(displayMode === 'inline' ? activeStyle : {}),
              ...mobileRightFlat,
            }}
          >
            {t('compareModes.inline', 'Inline Compare')}
          </Button>,
          tooltips.inline,
          'inline',
          displayMode === 'inline'
        )}
      {withTooltip(
        <Button
          icon={<LineChartOutlined />}
          onClick={() => handleModeChange(isTrendsMode ? 'normal' : 'trends')}
          style={{
            ...(isTrendsMode ? activeStyle : {}),
            ...mobileRightFlat,
          }}
        >
          {t('compareModes.trends', 'Trends')}
        </Button>,
        tooltips.trends,
        'trends',
        isTrendsMode
      )}
    </div>
  );
};

export default CompareModeTabs;
